import type {
  ProviderAllocationItem,
  ProviderAllocationOverview,
  ProviderAllocationState,
} from "../../api/providerAllocation";

/** One provider's editable setting: whether it is on, the total as typed, and the access groups ticked. */
export interface DraftEntry {
  enabled: boolean;
  amount: string;
  /** The vendor access group ids ticked; only meaningful for a provider with `supportsAccessGroups`. */
  accessGroups: string[];
}

export type Draft = Record<string, DraftEntry>;

export const emptyEntry: DraftEntry = { enabled: false, amount: "0", accessGroups: [] };

const wholeNumber = /^\d+$/;

/** Whether the amount text is a valid whole-USD figure. */
export function isValidAmount(text: string): boolean {
  return wholeNumber.test(text.trim());
}

/**
 * The least that can be allocated to a provider that is switched on: its seat fee, or the spend already incurred this
 * month if that is more. The server works this out; the form only shows it.
 */
export function minimumAmount(provider: ProviderAllocationState): number {
  return provider.minimumAmountUsd;
}

/**
 * Whether an enabled provider that is granted per access group has none ticked. At least one group is required to enable it
 * (AIA-109), the same rule the server applies, so a draft in this state cannot be submitted.
 */
export function isGroupSelectionMissing(
  provider: ProviderAllocationState,
  entry: DraftEntry,
): boolean {
  return (
    provider.supportsAccessGroups === true &&
    entry.enabled &&
    entry.accessGroups.length === 0
  );
}

/** Whether two group selections hold the same ids, in any order. */
function sameGroups(left: string[], right: string[]): boolean {
  return (
    left.length === right.length && left.every((group) => right.includes(group))
  );
}

/** Whether the amount text is a whole-USD figure that is not below the provider's minimum. */
export function isAmountAcceptable(
  provider: ProviderAllocationState,
  text: string,
): boolean {
  return (
    isValidAmount(text) &&
    Number.parseInt(text.trim(), 10) >= minimumAmount(provider)
  );
}

/**
 * The total a provider costs the person under the draft: zero when it is switched off, its fixed seat fee when it is a flat-fee
 * provider that is switched on, and the typed total otherwise. Null when a metered total has not been typed as a whole number.
 */
function enabledTotal(
  provider: ProviderAllocationState,
  entry: DraftEntry,
): number | null {
  if (!entry.enabled) {
    return 0;
  }

  if (!provider.supportsLimit) {
    return provider.minimumAmountUsd;
  }

  return isValidAmount(entry.amount)
    ? Number.parseInt(entry.amount, 10)
    : null;
}

/** Formats a USD figure without a currency symbol, to at most two decimals. */
export function formatNumber(amount: number): string {
  return String(Math.round(amount * 100) / 100);
}

/** Formats a USD figure with a leading dollar sign. */
export function formatUsd(amount: number): string {
  return `$${formatNumber(amount)}`;
}

/** Starts from `requested` settings when there are any, otherwise from what is currently saved. */
export function toDraft(
  overview: ProviderAllocationOverview,
  requested?: ProviderAllocationItem[],
): Draft {
  return Object.fromEntries(
    overview.providers.map((provider) => {
      const match = requested?.find(
        (item) =>
          item.vendorId.toLowerCase() === provider.vendorId.toLowerCase(),
      );
      return [
        provider.vendorId,
        match
          ? {
              enabled: match.enabled,
              amount: String(match.amountUsd),
              accessGroups: match.accessGroups ?? provider.accessGroups ?? [],
            }
          : {
              enabled: provider.enabled,
              amount: String(provider.amountUsd),
              accessGroups: provider.accessGroups ?? [],
            },
      ];
    }),
  );
}

/** The figures derived from the draft: cost committed by switched-off providers, what can be allocated, and what is left. */
export interface AllocationFigures {
  committed: number;
  allocable: number;
  allocated: number;
  remaining: number;
  hasInvalid: boolean;
}

/** Derives the figures for a draft. Every figure is a total, so no seat fee is deducted or added. */
export function computeFigures(
  overview: ProviderAllocationOverview,
  draft: Draft,
): AllocationFigures {
  const committed = overview.providers
    .filter((provider) => !(draft[provider.vendorId] ?? emptyEntry).enabled)
    .reduce((sum, provider) => sum + (provider.incurredCostUsd ?? 0), 0);
  const allocable = Math.max(0, Math.floor(overview.totalUsd - committed));
  const allocated = overview.providers.reduce(
    (sum, provider) =>
      sum +
      (enabledTotal(provider, draft[provider.vendorId] ?? emptyEntry) ?? 0),
    0,
  );
  const hasInvalid = overview.providers.some((provider) => {
    const entry = draft[provider.vendorId] ?? emptyEntry;
    return (
      (provider.supportsLimit &&
        entry.enabled &&
        !isAmountAcceptable(provider, entry.amount)) ||
      isGroupSelectionMissing(provider, entry)
    );
  });
  return {
    committed,
    allocable,
    allocated,
    remaining: allocable - allocated,
    hasInvalid,
  };
}

/** Whether the draft would send something different from `baseline`; an unparseable amount counts as a change. */
export function isDraftChanged(
  overview: ProviderAllocationOverview,
  draft: Draft,
  baseline: Draft,
): boolean {
  return overview.providers.some((provider) => {
    const now = draft[provider.vendorId] ?? emptyEntry;
    const then = baseline[provider.vendorId] ?? emptyEntry;
    return (
      now.enabled !== then.enabled ||
      enabledTotal(provider, now) !== enabledTotal(provider, then) ||
      (provider.supportsLimit &&
        now.enabled &&
        !isAmountAcceptable(provider, now.amount)) ||
      (provider.supportsAccessGroups === true &&
        now.enabled &&
        !sameGroups(now.accessGroups, then.accessGroups))
    );
  });
}

/** What would be sent to the server for the draft: each provider's total, which is what its vendor allowance is set to. */
export function toItems(
  overview: ProviderAllocationOverview,
  draft: Draft,
): ProviderAllocationItem[] {
  return overview.providers.map((provider) => {
    const entry = draft[provider.vendorId] ?? emptyEntry;
    return {
      vendorId: provider.vendorId,
      enabled: entry.enabled,
      amountUsd: enabledTotal(provider, entry) ?? 0,
      // Sent only where the server reads it: an enabled provider granted per group. Anything else omits the field.
      ...(provider.supportsAccessGroups === true && entry.enabled
        ? { accessGroups: [...entry.accessGroups] }
        : {}),
    };
  });
}
